/**
 * /admin/buyers — Compradores (clientes que pagaron)
 *
 * Segmentá por país / estado / plan y enviá invitación personalizada
 * por email (link único a /invitacion-exclusive).
 */

import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import {
  CheckCircle2,
  Copy,
  Download,
  Loader2,
  Mail,
  RefreshCw,
  Search,
  Send,
  Users,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

type BuyerOffer = {
  discount_percent?: number | null;
  promotion_code?: string | null;
  promotion_code_id?: string | null;
  llm_provider?: string | null;
  llm_model?: string | null;
  llm_ok?: boolean | null;
  kimi_bucket?: string | null;
  pattern_label?: string | null;
  decision_source?: string | null;
  outcomes?: string[] | null;
  has_discount?: boolean;
};

type BuyerRow = {
  id: string;
  email: string;
  name: string | null;
  level_code: string;
  status: string;
  payment_provider: string;
  payment_id: string | null;
  amount: number | null;
  currency: string | null;
  country: string | null;
  flow_name: string | null;
  started_at: string | null;
  expires_at: string | null;
  approved: boolean;
  invite_sends: number;
  last_invite_at: string | null;
  last_invite_code: string | null;
  has_telegram_invite: boolean;
  /** user_memberships | stripe_checkout_session */
  data_source?: string;
  stripe_session_id?: string | null;
  city?: string | null;
  province?: string | null;
  kimi_decision_id?: string | null;
  discount_percent?: number | null;
  offer?: BuyerOffer | null;
};

type OfferCoherence = {
  stripe_with_discount?: number;
  stripe_with_kimi_decision?: number;
  stripe_with_llm_provider?: number;
  kimi_decisions_joined?: number;
  kimi_db_ok?: boolean;
  kimi_db_error?: string | null;
};

type BuyersResponse = {
  success: boolean;
  brand_source: string;
  generated_at: string;
  days: number;
  sources?: {
    user_memberships?: number;
    stripe_checkout_sessions?: number;
    stripe_only_merged?: number;
    stripe_ok?: boolean;
    stripe_error?: string | null;
    stripe_scanned_sessions?: number;
    stripe_brand_sessions_matched?: number;
    note?: string;
  };
  offer_coherence?: OfferCoherence;
  totals: {
    buyers: number;
    with_invite_sent: number;
    awaiting_invite: number;
    by_country: Record<string, number>;
    by_status: Record<string, number>;
    by_level: Record<string, number>;
  };
  buyers: BuyerRow[];
};

/** Resolve discount % from top-level or nested offer (API may lag). */
function getDiscountPercent(b: BuyerRow): number | null {
  const v = b.discount_percent ?? b.offer?.discount_percent;
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

function getPromoCode(b: BuyerRow): string | null {
  const code = b.offer?.promotion_code;
  return code && String(code).trim() ? String(code).trim() : null;
}

function getLlmProvider(b: BuyerRow): string | null {
  const p = b.offer?.llm_provider;
  return p && String(p).trim() ? String(p).trim().toLowerCase() : null;
}

function getKimiBucket(b: BuyerRow): string | null {
  const k = b.offer?.kimi_bucket;
  return k && String(k).trim() ? String(k).trim().toLowerCase() : null;
}

function hasPaidOutcome(b: BuyerRow): boolean {
  const outcomes = b.offer?.outcomes;
  return Array.isArray(outcomes) && outcomes.some((o) => String(o).toLowerCase() === "paid");
}

function fmtTime(iso: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-AR", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function fmtAmount(amount: number | null, currency: string | null): string {
  if (amount == null) return "—";
  const c = (currency || "USD").toUpperCase();
  // Stripe amounts are usually cents for USD
  const value = c === "USD" || c === "EUR" || c === "GBP" ? amount / 100 : amount / 100;
  try {
    return new Intl.NumberFormat("es-AR", { style: "currency", currency: c }).format(value);
  } catch {
    return `${value} ${c}`;
  }
}

function escCsv(v: unknown): string {
  if (v == null) return "";
  const s = String(v);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

export default function BuyersAdmin() {
  const { getAuthHeaders, isLoaded } = useAdminAuth();
  const { toast } = useToast();

  const [data, setData] = useState<BuyersResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [days, setDays] = useState("7");
  const [country, setCountry] = useState("all");
  const [status, setStatus] = useState("all");
  const [level, setLevel] = useState("all");
  const [search, setSearch] = useState("");
  const [onlyAwaiting, setOnlyAwaiting] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [personalMessage, setPersonalMessage] = useState("");
  const [destination, setDestination] = useState("invitacion_exclusiva"); // → /invitacion-exclusive
  const [sending, setSending] = useState(false);
  const [reconciling, setReconciling] = useState(false);
  /** Default ON for safety — dry run until operator unchecks + confirms. */
  const [reconcileDryRun, setReconcileDryRun] = useState(true);

  const load = useCallback(async () => {
    if (!isLoaded) return;
    setLoading(true);
    setError(null);
    try {
      const headers = await getAuthHeaders();
      const params = new URLSearchParams({
        days,
        limit: "1000",
      });
      if (country !== "all") params.set("country", country);
      if (status !== "all") params.set("status", status);
      if (level !== "all") params.set("level", level);
      if (search.trim()) params.set("q", search.trim());
      if (onlyAwaiting) params.set("only_awaiting_invite", "1");

      const res = await fetch(`/api/admin/buyers?${params}`, { headers });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.error || `HTTP ${res.status}`);
      }
      const json = (await res.json()) as BuyersResponse;
      setData(json);
      setSelected(new Set());
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al cargar");
    } finally {
      setLoading(false);
    }
  }, [
    getAuthHeaders,
    isLoaded,
    days,
    country,
    status,
    level,
    search,
    onlyAwaiting,
  ]);

  useEffect(() => {
    void load();
  }, [load]);

  const buyers = data?.buyers ?? [];
  const countries = useMemo(() => {
    const keys = Object.keys(data?.totals.by_country || {}).sort(
      (a, b) => (data!.totals.by_country[b] || 0) - (data!.totals.by_country[a] || 0),
    );
    return keys;
  }, [data]);

  const allSelected =
    buyers.length > 0 && buyers.every((b) => selected.has(b.email));

  function toggleAll() {
    if (allSelected) {
      setSelected(new Set());
      return;
    }
    setSelected(new Set(buyers.map((b) => b.email)));
  }

  function toggleOne(email: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(email)) next.delete(email);
      else next.add(email);
      return next;
    });
  }

  async function sendInvites() {
    if (selected.size === 0) {
      toast({
        title: "Seleccioná al menos un comprador",
        variant: "destructive",
      });
      return;
    }
    setSending(true);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch("/api/admin/buyers", {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "send_invitation",
          emails: [...selected],
          personal_message: personalMessage.trim() || undefined,
          destination,
          expiration_days: 14,
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(json.error || `HTTP ${res.status}`);
      }
      toast({
        title: `Invitaciones enviadas: ${json.sent ?? 0}`,
        description:
          json.failed > 0
            ? `${json.failed} fallaron — revisá el detalle`
            : "Cada uno recibió un link único por email",
      });
      setPersonalMessage("");
      await load();
    } catch (err) {
      toast({
        title: "No se pudo enviar",
        description: err instanceof Error ? err.message : "Error",
        variant: "destructive",
      });
    } finally {
      setSending(false);
    }
  }

  async function reconcileStripeBuyers() {
    if (!reconcileDryRun) {
      const ok = window.confirm(
        "¿Escribir en la base? Se crearán/renovarán membresías faltantes desde Checkout Sessions de este brand (metadata.source). Esta acción no es solo simulación.",
      );
      if (!ok) return;
    }

    setReconciling(true);
    try {
      const headers = await getAuthHeaders();
      const daysNum = Math.max(1, parseInt(days, 10) || 7);
      const res = await fetch("/api/admin/reconcile-stripe-buyers", {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({
          days: daysNum,
          limit: 200,
          dry_run: reconcileDryRun,
        }),
      });

      if (res.status === 404) {
        toast({
          title: "Endpoint no disponible",
          description:
            "POST /api/admin/reconcile-stripe-buyers no existe (404). ¿Deploy del API pendiente?",
          variant: "destructive",
        });
        return;
      }

      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(json.error || json.message || `HTTP ${res.status}`);
      }

      const created = json.memberships_created ?? 0;
      const renewed = json.memberships_renewed ?? 0;
      const skipped = json.memberships_skipped ?? 0;
      const failed = json.memberships_failed ?? 0;
      const kimiPaid = json.kimi_paid_stamped ?? 0;
      const buyersCount = json.buyers ?? 0;
      const prefix = reconcileDryRun ? "Simulación" : "Reconciliación";

      toast({
        title: `${prefix} Stripe → DB`,
        description: [
          `${buyersCount} compradores`,
          `creadas: ${created}`,
          `renovadas: ${renewed}`,
          `omitidas: ${skipped}`,
          failed > 0 ? `fallidas: ${failed}` : null,
          `kimi paid: ${kimiPaid}`,
          reconcileDryRun ? "(sin escribir)" : null,
        ]
          .filter(Boolean)
          .join(" · "),
      });

      if (!reconcileDryRun) {
        await load();
      }
    } catch (err) {
      toast({
        title: "No se pudo reconciliar Stripe → DB",
        description: err instanceof Error ? err.message : "Error",
        variant: "destructive",
      });
    } finally {
      setReconciling(false);
    }
  }

  function exportCsv() {
    const rows = [
      [
        "email",
        "name",
        "country",
        "city",
        "province",
        "level",
        "status",
        "amount",
        "currency",
        "provider",
        "flow",
        "data_source",
        "stripe_session_id",
        "payment_id",
        "started_at",
        "approved",
        "invite_sends",
        "last_invite_at",
        "discount_percent",
        "promotion_code",
        "kimi_decision_id",
        "llm_provider",
        "kimi_bucket",
      ].join(","),
      ...buyers.map((b) =>
        [
          escCsv(b.email),
          escCsv(b.name),
          escCsv(b.country),
          escCsv(b.city),
          escCsv(b.province),
          escCsv(b.level_code),
          escCsv(b.status),
          escCsv(b.amount),
          escCsv(b.currency),
          escCsv(b.payment_provider),
          escCsv(b.flow_name),
          escCsv(b.data_source || "user_memberships"),
          escCsv(b.stripe_session_id),
          escCsv(b.payment_id),
          escCsv(b.started_at),
          escCsv(b.approved),
          escCsv(b.invite_sends),
          escCsv(b.last_invite_at),
          escCsv(getDiscountPercent(b)),
          escCsv(getPromoCode(b)),
          escCsv(b.kimi_decision_id),
          escCsv(getLlmProvider(b) ?? b.offer?.llm_provider),
          escCsv(getKimiBucket(b) ?? b.offer?.kimi_bucket),
        ].join(","),
      ),
    ];
    const blob = new Blob([rows.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `compradores-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  async function copyEmails() {
    const list = buyers.map((b) => b.email).join("\n");
    await navigator.clipboard.writeText(list);
    toast({ title: `${buyers.length} emails copiados` });
  }

  return (
    <div className="space-y-6 pb-16">
      <AdminPageHeader
        title="Compradores"
        description="Clientes que pagaron este brand (Stripe Checkout + membresías). Segmentá y enviá invitación personalizada por email."
      />

      <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
        <span>
          Brand:{" "}
          <code className="rounded bg-muted px-1">{data?.brand_source || "…"}</code>
        </span>
        <span>·</span>
        <Link to="/admin/memberships" className="underline-offset-2 hover:underline">
          Aprobaciones
        </Link>
        <span>·</span>
        <Link to="/admin/invitaciones" className="underline-offset-2 hover:underline">
          Códigos invite
        </Link>
        <span>·</span>
        <Link to="/admin/recovery-leads" className="underline-offset-2 hover:underline">
          Leads recovery
        </Link>
      </div>

      {/* Stats */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Compradores</CardDescription>
            <CardTitle className="text-2xl tabular-nums">
              {data?.totals.buyers ?? "—"}
            </CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Sin invite enviada</CardDescription>
            <CardTitle className="text-2xl tabular-nums">
              {data?.totals.awaiting_invite ?? "—"}
            </CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Ya recibieron invite</CardDescription>
            <CardTitle className="text-2xl tabular-nums">
              {data?.totals.with_invite_sent ?? "—"}
            </CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Fuentes de datos</CardDescription>
            <CardTitle className="text-sm font-medium leading-snug pt-1">
              {data?.sources ? (
                <span className="tabular-nums text-base">
                  DB {data.sources.user_memberships ?? 0}
                  <span className="text-muted-foreground font-normal"> · </span>
                  Stripe {data.sources.stripe_checkout_sessions ?? 0}
                  {(data.sources.stripe_only_merged ?? 0) > 0 && (
                    <span className="text-amber-700 dark:text-amber-400 font-normal">
                      {" "}
                      (+{data.sources.stripe_only_merged} solo Stripe)
                    </span>
                  )}
                </span>
              ) : (
                "—"
              )}
            </CardTitle>
            {data?.sources?.stripe_ok === false && (
              <p className="text-[11px] text-destructive mt-1">
                Stripe: {data.sources.stripe_error || "error"}
              </p>
            )}
            {data?.sources?.stripe_ok && (
              <p className="text-[11px] text-muted-foreground mt-1">
                Escaneadas {data.sources.stripe_scanned_sessions ?? 0} sessions ·
                brand {data.sources.stripe_brand_sessions_matched ?? 0}
              </p>
            )}
          </CardHeader>
        </Card>
      </div>

      {/* Offer / LLM coherence */}
      {data?.offer_coherence != null && (
        <Card className="border-violet-500/20">
          <CardHeader className="pb-2">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <CardTitle className="text-base">Coherencia Offer / LLM</CardTitle>
                <CardDescription>
                  Cuántas compras Stripe traen descuento, decisión Kimi y proveedor LLM.
                </CardDescription>
              </div>
              <Link
                to="/admin/kimi-patterns"
                className="text-xs font-medium text-violet-700 dark:text-violet-300 underline-offset-2 hover:underline shrink-0"
              >
                Canvas Offer LLM
              </Link>
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm tabular-nums">
              <span>
                <span className="text-muted-foreground">Con descuento:</span>{" "}
                <strong>{data.offer_coherence.stripe_with_discount ?? "—"}</strong>
              </span>
              <span>
                <span className="text-muted-foreground">Con decisión Kimi:</span>{" "}
                <strong>{data.offer_coherence.stripe_with_kimi_decision ?? "—"}</strong>
              </span>
              <span>
                <span className="text-muted-foreground">Con LLM provider:</span>{" "}
                <strong>{data.offer_coherence.stripe_with_llm_provider ?? "—"}</strong>
              </span>
              <span>
                <span className="text-muted-foreground">Kimi join:</span>{" "}
                <strong>{data.offer_coherence.kimi_decisions_joined ?? "—"}</strong>
              </span>
            </div>
            {data.offer_coherence.kimi_db_ok === false && (
              <p className="text-[11px] text-amber-700 dark:text-amber-400">
                Kimi DB no disponible
                {data.offer_coherence.kimi_db_error
                  ? `: ${data.offer_coherence.kimi_db_error}`
                  : ""}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Users className="h-4 w-4" /> Segmentar
          </CardTitle>
          <CardDescription>
            Brand isolation: <code className="text-xs">metadata.source</code> /{" "}
            <code className="text-xs">brand_source</code> = este sitio. Stripe
            live cubre hasta 90 días (cuenta compartida).
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
          <div className="space-y-1.5 min-w-[120px]">
            <Label className="text-xs">Días</Label>
            <Select value={days} onValueChange={setDays}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="2">48 horas</SelectItem>
                <SelectItem value="7">7 días</SelectItem>
                <SelectItem value="30">30 días</SelectItem>
                <SelectItem value="90">90 días</SelectItem>
                <SelectItem value="180">180 días</SelectItem>
                <SelectItem value="365">1 año</SelectItem>
                <SelectItem value="3650">Todo</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5 min-w-[120px]">
            <Label className="text-xs">País</Label>
            <Select value={country} onValueChange={setCountry}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {countries.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c} ({data?.totals.by_country[c]})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5 min-w-[140px]">
            <Label className="text-xs">Estado</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="active">active</SelectItem>
                <SelectItem value="pending_approval">pending_approval</SelectItem>
                <SelectItem value="expired">expired</SelectItem>
                <SelectItem value="cancelled">cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5 min-w-[120px]">
            <Label className="text-xs">Plan</Label>
            <Select value={level} onValueChange={setLevel}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {Object.keys(data?.totals.by_level || {}).map((lv) => (
                  <SelectItem key={lv} value={lv}>
                    {lv} ({data?.totals.by_level[lv]})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5 flex-1 min-w-[180px]">
            <Label className="text-xs">Buscar email / nombre</Label>
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                className="pl-9"
                placeholder="email o nombre…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") void load();
                }}
              />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm pb-2 cursor-pointer select-none">
            <input
              type="checkbox"
              className="rounded border"
              checked={onlyAwaiting}
              onChange={(e) => setOnlyAwaiting(e.target.checked)}
            />
            Solo sin invite
          </label>
          <Button type="button" variant="secondary" onClick={() => void load()} disabled={loading}>
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            <span className="ml-2">Actualizar</span>
          </Button>
          <Button type="button" variant="outline" onClick={exportCsv} disabled={!buyers.length}>
            <Download className="h-4 w-4 mr-2" />
            CSV
          </Button>
          <Button type="button" variant="outline" onClick={() => void copyEmails()} disabled={!buyers.length}>
            <Copy className="h-4 w-4 mr-2" />
            Emails
          </Button>
          <div className="flex flex-wrap items-center gap-2 border-l pl-3 ml-1">
            <label
              className="flex items-center gap-2 text-sm cursor-pointer select-none"
              title="Si está marcado, solo simula sin escribir en la base"
            >
              <input
                type="checkbox"
                className="rounded border"
                checked={reconcileDryRun}
                onChange={(e) => setReconcileDryRun(e.target.checked)}
                disabled={reconciling}
              />
              Solo simular
            </label>
            <Button
              type="button"
              variant="outline"
              onClick={() => void reconcileStripeBuyers()}
              disabled={reconciling || !isLoaded}
              title="Crea membresías faltantes desde Checkout Sessions de este brand (metadata.source)."
            >
              {reconciling ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
              <span className="ml-2">Reconciliar Stripe → DB</span>
            </Button>
          </div>
          <p className="w-full text-xs text-muted-foreground basis-full">
            Reconciliar: crea membresías faltantes desde Checkout Sessions de este brand
            (metadata.source). Usá «Solo simular» primero; al desmarcarlo pide confirmación antes de
            escribir.
          </p>
        </CardContent>
      </Card>

      {/* Send panel */}
      <Card className="border-amber-500/30">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Mail className="h-4 w-4" /> Invitación personalizada
          </CardTitle>
          <CardDescription>
            Genera un código único (1 uso) y lo manda a{" "}
            <code className="text-xs">mail.anaafernandez.com</code>. Link limpio, sin UTM.
            Destino default: <code className="text-xs">/invitacion-exclusive</code>.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-3 items-end">
            <div className="space-y-1.5 min-w-[200px]">
              <Label className="text-xs">Destino del link</Label>
              <Select value={destination} onValueChange={setDestination}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="invitacion_exclusiva">
                    /invitacion-exclusive (recomendado)
                  </SelectItem>
                  <SelectItem value="invitacion_premium">/invitacion-premium</SelectItem>
                  <SelectItem value="acceso">/acceso</SelectItem>
                  <SelectItem value="exclusive">/exclusive (intl)</SelectItem>
                  <SelectItem value="exclusive_ar">/exclusive-ar (AR)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <p className="text-sm text-muted-foreground pb-2">
              Seleccionados: <strong className="text-foreground">{selected.size}</strong>
            </p>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Mensaje personal (opcional)</Label>
            <Textarea
              placeholder="Ej: Hola, te dejé el acceso listo — abrí el link cuando quieras…"
              value={personalMessage}
              onChange={(e) => setPersonalMessage(e.target.value)}
              rows={3}
              maxLength={800}
              className="resize-y"
            />
          </div>
          <Button
            type="button"
            onClick={() => void sendInvites()}
            disabled={sending || selected.size === 0}
            className="w-full sm:w-auto"
          >
            {sending ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Send className="h-4 w-4 mr-2" />
            )}
            Enviar invitación a {selected.size || "…"}
          </Button>
        </CardContent>
      </Card>

      {error && (
        <p className="text-sm text-destructive rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2">
          {error}
        </p>
      )}

      {/* Table */}
      <Card>
        <CardHeader className="pb-2 flex flex-row items-center justify-between gap-2">
          <div>
            <CardTitle className="text-base">Lista</CardTitle>
            <CardDescription>
              {loading ? "Cargando…" : `${buyers.length} filas`}
            </CardDescription>
          </div>
          <Button type="button" size="sm" variant="ghost" onClick={toggleAll} disabled={!buyers.length}>
            {allSelected ? "Quitar todos" : "Seleccionar todos"}
          </Button>
        </CardHeader>
        <CardContent className="overflow-x-auto -mx-1 px-1">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs text-muted-foreground">
                <th className="py-2 pr-2 w-8" />
                <th className="py-2 pr-3 font-medium">Email</th>
                <th className="py-2 pr-3 font-medium">Oferta</th>
                <th className="py-2 pr-3 font-medium hidden sm:table-cell">País</th>
                <th className="py-2 pr-3 font-medium hidden md:table-cell">Plan</th>
                <th className="py-2 pr-3 font-medium">Estado</th>
                <th className="py-2 pr-3 font-medium hidden lg:table-cell">Monto</th>
                <th className="py-2 pr-3 font-medium hidden xl:table-cell">Origen</th>
                <th className="py-2 pr-3 font-medium hidden md:table-cell">Compra</th>
                <th className="py-2 pr-3 font-medium">Invite</th>
              </tr>
            </thead>
            <tbody>
              {buyers.map((b) => {
                const discount = getDiscountPercent(b);
                const promo = getPromoCode(b);
                const llm = getLlmProvider(b);
                const bucket = getKimiBucket(b);
                const showBucket = bucket === "treatment" || bucket === "control";
                const paid = hasPaidOutcome(b);
                const hasOfferBits =
                  discount != null || !!promo || !!llm || showBucket || paid;

                return (
                <tr
                  key={b.id}
                  className={cn(
                    "border-b border-border/60 hover:bg-muted/40",
                    selected.has(b.email) && "bg-amber-500/5",
                  )}
                >
                  <td className="py-2.5 pr-2 align-middle">
                    <input
                      type="checkbox"
                      className="rounded border"
                      checked={selected.has(b.email)}
                      onChange={() => toggleOne(b.email)}
                      aria-label={`Seleccionar ${b.email}`}
                    />
                  </td>
                  <td className="py-2.5 pr-3 align-middle">
                    <div className="font-medium break-all">{b.email}</div>
                    {b.name && (
                      <div className="text-xs text-muted-foreground">{b.name}</div>
                    )}
                    {(b.city || b.province) && (
                      <div className="text-[11px] text-muted-foreground">
                        {[b.city, b.province].filter(Boolean).join(", ")}
                      </div>
                    )}
                    {b.flow_name && (
                      <div className="text-[11px] text-muted-foreground font-mono">
                        {b.flow_name}
                      </div>
                    )}
                  </td>
                  <td className="py-2.5 pr-3 align-middle">
                    {hasOfferBits ? (
                      <div className="flex flex-wrap items-center gap-1 max-w-[200px]">
                        {discount != null && (
                          <Badge
                            variant="secondary"
                            className="text-[10px] font-semibold tabular-nums"
                            title="Descuento aplicado"
                          >
                            −{discount}%
                          </Badge>
                        )}
                        {promo && (
                          <span
                            className="font-mono text-[10px] text-muted-foreground truncate max-w-[100px]"
                            title={promo}
                          >
                            {promo}
                          </span>
                        )}
                        {llm && (
                          <Badge
                            variant="outline"
                            className="text-[10px] font-normal border-violet-400/60 text-violet-700 dark:text-violet-300"
                            title={b.offer?.llm_model || undefined}
                          >
                            LLM: {llm}
                          </Badge>
                        )}
                        {showBucket && (
                          <Badge
                            variant="outline"
                            className="text-[9px] font-normal px-1 py-0 capitalize text-muted-foreground"
                          >
                            {bucket}
                          </Badge>
                        )}
                        {paid && (
                          <CheckCircle2
                            className="h-3.5 w-3.5 text-emerald-600 shrink-0"
                            aria-label="Outcome paid"
                            title="Outcome: paid"
                          />
                        )}
                      </div>
                    ) : (
                      <span className="text-muted-foreground text-xs">—</span>
                    )}
                  </td>
                  <td className="py-2.5 pr-3 align-middle hidden sm:table-cell">
                    <Badge variant="outline" className="font-mono text-[11px]">
                      {b.country || "??"}
                    </Badge>
                  </td>
                  <td className="py-2.5 pr-3 align-middle hidden md:table-cell text-xs">
                    {b.level_code}
                  </td>
                  <td className="py-2.5 pr-3 align-middle">
                    <Badge
                      variant={
                        b.status === "active"
                          ? "default"
                          : b.status === "pending_approval"
                            ? "secondary"
                            : "outline"
                      }
                      className="text-[11px]"
                    >
                      {b.status}
                    </Badge>
                    {b.approved && (
                      <CheckCircle2 className="inline h-3.5 w-3.5 ml-1 text-emerald-600" />
                    )}
                  </td>
                  <td className="py-2.5 pr-3 align-middle hidden lg:table-cell tabular-nums text-xs">
                    {fmtAmount(b.amount, b.currency)}
                  </td>
                  <td className="py-2.5 pr-3 align-middle hidden xl:table-cell">
                    <Badge
                      variant="outline"
                      className={cn(
                        "text-[10px] font-normal",
                        b.data_source === "stripe_checkout_session"
                          ? "border-violet-400/50 text-violet-700 dark:text-violet-300"
                          : "text-muted-foreground",
                      )}
                      title={b.stripe_session_id || b.payment_id || undefined}
                    >
                      {b.data_source === "stripe_checkout_session"
                        ? "Stripe"
                        : "DB"}
                    </Badge>
                  </td>
                  <td className="py-2.5 pr-3 align-middle hidden md:table-cell text-xs text-muted-foreground whitespace-nowrap">
                    {fmtTime(b.started_at)}
                  </td>
                  <td className="py-2.5 pr-3 align-middle text-xs">
                    {b.invite_sends > 0 ? (
                      <span className="text-emerald-700 dark:text-emerald-400">
                        {b.invite_sends}× · {fmtTime(b.last_invite_at)}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                </tr>
                );
              })}
              {!loading && buyers.length === 0 && (
                <tr>
                  <td colSpan={10} className="py-10 text-center text-muted-foreground">
                    No hay compradores con estos filtros.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
